'use client'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { getCredentials, refresh, removeCredentials } from '../../session'
import { getConfig } from '@/tools/config'
import { clearUserId } from '@/tools/device-detection/providers/incognia'
import { fetcher, removeAuthorization } from '@/tools/fetcher/client'
import { glovoRum } from '@/tools/o11y/client'
import { useWhatsUp } from '@/tools/whats-up'

type AuthState = {
  isLoggedIn: boolean
  logOut: () => Promise<void>
  userDidLogIn: () => Promise<void>
}

// Create the context object
const AuthContext = createContext<AuthState>({
  isLoggedIn: false,
  logOut: async () => undefined,
  userDidLogIn: async () => undefined,
})

// Create the provider component
export function AuthProvider({
  children,
  isLoggedIn,
  shouldRefreshToken = false,
}: {
  children: React.ReactNode
  isLoggedIn: boolean
  shouldRefreshToken?: boolean
}) {
  const [loggedIn, setLoggedIn] = useState(isLoggedIn ?? false)
  const { getWhatsUp } = useWhatsUp()

  const value: AuthState = useMemo(
    () => ({
      isLoggedIn: loggedIn,
      logOut: async () => {
        const cookieCredentials = getCredentials()
        if (cookieCredentials) {
          const { apiUrl } = getConfig()
          await fetcher.post(`${apiUrl}/oauth/revoke`, { token: cookieCredentials?.refreshToken })
          removeCredentials()
          await clearUserId()
        }
        removeAuthorization()
        setLoggedIn(false)
        await getWhatsUp()
      },
      userDidLogIn: async () => {
        setLoggedIn(true)
        await getWhatsUp()
      },
    }),

    [getWhatsUp, loggedIn]
  )

  // eslint-disable-next-line o11y-guard/error-tracking
  useEffect(() => {
    const refreshAuth = async () => {
      await refresh()
      setLoggedIn(true)
      await getWhatsUp()
    }

    if (shouldRefreshToken) {
      glovoRum.addAction('auth_state_rehydrated_on_client')
      refreshAuth().catch((e) => glovoRum.addError(e, {}))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefreshToken])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside a <AuthProvider>')
  }
  return context
}
